import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  Calendar as CalendarIcon, 
  Clock, 
  CheckCircle, 
  ArrowRight,
  Loader2,
  Lock,
  Sparkles,
  Video,
  Target,
  Award,
  MessageSquare,
  ChevronRight,
  X,
  Star,
  Briefcase
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SESSION_TYPE_ICONS = {
  career_strategy: Target,
  resume_optimization: Briefcase,
  interview_prep: MessageSquare,
  linkedin_branding: Star,
  ai_job_search: Sparkles
};

const Coaching = () => {
  const { token } = useAuth();
  const { hasFullAccess, isPreviewMode } = useSubscription();
  const [activeTab, setActiveTab] = useState('overview');
  const [coaches, setCoaches] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availability, setAvailability] = useState({});
  const [userSessions, setUserSessions] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, [token]);

  const fetchInitialData = async () => {
    try {
      const [coachesRes, typesRes, sessionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/coaching/coaches`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/coaching/session-types`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/coaching/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCoaches(coachesRes.data);
      setSessionTypes(typesRes.data);
      setUserSessions(sessionsRes.data);
      
      // Auto-select first coach
      if (coachesRes.data.length > 0) {
        setSelectedCoach(coachesRes.data[0]);
        fetchAvailability(coachesRes.data[0].coach_id);
      }
    } catch (error) {
      console.error('Failed to fetch coaching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async (coachId) => {
    try {
      const response = await axios.get(`${API_URL}/api/coaching/availability/${coachId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailability(response.data.availability || {});
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  };

  const handleBookSession = async () => {
    if (!hasFullAccess) {
      toast.error('Activate your membership to book coaching sessions');
      return;
    }

    if (!selectedCoach || !selectedType || !selectedDate || !selectedSlot) {
      toast.error('Please complete all booking details');
      return;
    }

    setBookingLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      await axios.post(`${API_URL}/api/coaching/book`, {
        coach_id: selectedCoach.coach_id,
        session_type: selectedType,
        date: dateStr,
        start_time: selectedSlot.start_time
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Coaching session booked successfully!');
      setShowConfirmation(true);
      setActiveTab('sessions');
      
      // Refresh data
      fetchInitialData();
      if (selectedCoach) {
        fetchAvailability(selectedCoach.coach_id);
      }
      
      // Reset selection
      setSelectedSlot(null);
      setSelectedType('');
      setSelectedDate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book session');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancelSession = async (sessionId) => {
    if (!confirm('Are you sure you want to cancel this session?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/coaching/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session canceled');
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel session');
    }
  };

  const getAvailableDates = () => {
    return Object.keys(availability).map(dateStr => new Date(dateStr));
  };

  const getTimeSlotsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return availability[dateStr] || [];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getSessionTypeName = (typeId) => {
    const type = sessionTypes.find(t => t.id === typeId);
    return type?.name || typeId;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="coaching-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-navy-900 dark:text-white mb-2">
            Atlas Coaching
          </h1>
          <p className="text-navy-600 dark:text-navy-300">
            Work directly with an Atlas Career Coach to accelerate your career growth
          </p>
        </div>

        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-navy-800 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/50 rounded-xl flex items-center justify-center">
                    <Lock className="w-7 h-7 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-navy-900 dark:text-white">
                      Membership Required
                    </h3>
                    <p className="text-navy-600 dark:text-navy-300">
                      Activate your Atlas membership to book coaching sessions
                    </p>
                  </div>
                </div>
                <Link to="/membership">
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Activate Membership
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: Users },
            { id: 'book', label: 'Book Session', icon: CalendarIcon },
            { id: 'sessions', label: 'My Sessions', icon: Clock }
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={activeTab === tab.id 
                ? 'bg-amber-600 hover:bg-amber-700' 
                : 'dark:border-navy-600 dark:text-white dark:hover:bg-navy-700'}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
              {tab.id === 'sessions' && userSessions.upcoming.length > 0 && (
                <Badge className="ml-2 bg-amber-500">{userSessions.upcoming.length}</Badge>
              )}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Hero Section */}
            <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 overflow-hidden">
              <div className="bg-gradient-to-r from-navy-900 to-navy-800 p-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-24 h-24 bg-amber-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-12 h-12 text-white" />
                  </div>
                  <div className="text-center md:text-left">
                    <h2 className="font-heading text-2xl font-bold text-white mb-2">
                      Book Your Atlas Coaching Session
                    </h2>
                    <p className="text-navy-200 max-w-xl">
                      Get personalized guidance from experienced career coaches who specialize in 
                      military-to-civilian transitions. Our coaches have helped thousands of veterans 
                      achieve their career goals.
                    </p>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Video className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900 dark:text-white">Virtual Sessions</h4>
                      <p className="text-sm text-navy-500 dark:text-navy-400">
                        One-on-one video coaching from anywhere
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900 dark:text-white">60-Minute Sessions</h4>
                      <p className="text-sm text-navy-500 dark:text-navy-400">
                        Dedicated time for in-depth coaching
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Award className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900 dark:text-white">Expert Coaches</h4>
                      <p className="text-sm text-navy-500 dark:text-navy-400">
                        Specialized in veteran transitions
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Session Types */}
            <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                  <Target className="w-5 h-5 text-amber-600" />
                  Session Types
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sessionTypes.map((type) => {
                    const Icon = SESSION_TYPE_ICONS[type.id] || Target;
                    return (
                      <div
                        key={type.id}
                        className="p-4 border border-navy-100 dark:border-navy-600 rounded-xl hover:border-amber-300 dark:hover:border-amber-600 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-navy-100 dark:bg-navy-700 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-navy-600 dark:text-navy-300" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-navy-900 dark:text-white">{type.name}</h4>
                            <p className="text-sm text-navy-500 dark:text-navy-400">{type.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Coach Profile */}
            {coaches.length > 0 && (
              <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                    <Users className="w-5 h-5 text-amber-600" />
                    Your Coach
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {coaches.map((coach) => (
                    <div key={coach.coach_id} className="flex flex-col md:flex-row gap-6 items-start">
                      <div className="w-24 h-24 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Users className="w-12 h-12 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-semibold text-navy-900 dark:text-white">
                          {coach.name}
                        </h3>
                        <p className="text-amber-600 dark:text-amber-400 mb-3">{coach.title}</p>
                        <p className="text-navy-600 dark:text-navy-300 mb-4">{coach.bio}</p>
                        <div className="flex flex-wrap gap-2">
                          {coach.specialties?.map((specialty, idx) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className="border-navy-200 dark:border-navy-600 text-navy-600 dark:text-navy-300"
                            >
                              {specialty}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* CTA */}
            <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
              <CardContent className="p-6 text-center">
                <h3 className="font-heading text-xl font-semibold text-navy-900 dark:text-white mb-2">
                  Ready to Accelerate Your Career?
                </h3>
                <p className="text-navy-600 dark:text-navy-300 mb-4">
                  Book your first coaching session and take the next step in your transition.
                </p>
                <Button
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => setActiveTab('book')}
                  data-testid="book-session-cta"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Book a Session
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Book Session Tab */}
        {activeTab === 'book' && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Selection */}
            <div className="lg:col-span-2 space-y-6">
              {/* Step 1: Session Type */}
              <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                    <div className="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">
                      1
                    </div>
                    Select Session Type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Select
                    value={selectedType}
                    onValueChange={setSelectedType}
                    disabled={!hasFullAccess}
                  >
                    <SelectTrigger className="w-full dark:bg-navy-700 dark:border-navy-600" data-testid="session-type-select">
                      <SelectValue placeholder="Choose a session type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {sessionTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{type.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedType && (
                    <p className="text-sm text-navy-500 dark:text-navy-400 mt-2">
                      {sessionTypes.find(t => t.id === selectedType)?.description}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Step 2: Date Selection */}
              <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                    <div className="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">
                      2
                    </div>
                    Select Date
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setSelectedSlot(null);
                      }}
                      disabled={(date) => {
                        if (!hasFullAccess) return true;
                        const dateStr = date.toISOString().split('T')[0];
                        return !availability[dateStr];
                      }}
                      className="rounded-md border dark:border-navy-600"
                      data-testid="booking-calendar"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Step 3: Time Selection */}
              {selectedDate && (
                <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                      <div className="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">
                        3
                      </div>
                      Select Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {getTimeSlotsForDate(selectedDate).map((slot) => (
                        <Button
                          key={slot.slot_id}
                          variant={selectedSlot?.slot_id === slot.slot_id ? 'default' : 'outline'}
                          className={
                            selectedSlot?.slot_id === slot.slot_id
                              ? 'bg-amber-600 hover:bg-amber-700'
                              : 'dark:border-navy-600 dark:text-white dark:hover:bg-navy-700'
                          }
                          onClick={() => setSelectedSlot(slot)}
                          disabled={!hasFullAccess}
                          data-testid={`time-slot-${slot.start_time}`}
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          {formatTime(slot.start_time)}
                        </Button>
                      ))}
                    </div>
                    {getTimeSlotsForDate(selectedDate).length === 0 && (
                      <p className="text-center text-navy-500 dark:text-navy-400 py-4">
                        No available slots for this date
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Summary */}
            <div className="lg:col-span-1">
              <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 sticky top-6">
                <CardHeader>
                  <CardTitle className="text-navy-900 dark:text-white">Booking Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Coach */}
                  <div className="flex items-center gap-3 p-3 bg-navy-50 dark:bg-navy-700/50 rounded-lg">
                    <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-navy-500 dark:text-navy-400">Coach</p>
                      <p className="font-medium text-navy-900 dark:text-white">
                        {selectedCoach?.name || 'Atlas Career Coach'}
                      </p>
                    </div>
                  </div>

                  {/* Session Type */}
                  <div className="p-3 bg-navy-50 dark:bg-navy-700/50 rounded-lg">
                    <p className="text-xs text-navy-500 dark:text-navy-400">Session Type</p>
                    <p className="font-medium text-navy-900 dark:text-white">
                      {selectedType ? getSessionTypeName(selectedType) : 'Not selected'}
                    </p>
                  </div>

                  {/* Date & Time */}
                  <div className="p-3 bg-navy-50 dark:bg-navy-700/50 rounded-lg">
                    <p className="text-xs text-navy-500 dark:text-navy-400">Date & Time</p>
                    <p className="font-medium text-navy-900 dark:text-white">
                      {selectedDate ? formatDate(selectedDate.toISOString().split('T')[0]) : 'Not selected'}
                    </p>
                    {selectedSlot && (
                      <p className="text-amber-600 dark:text-amber-400">
                        {formatTime(selectedSlot.start_time)} - {formatTime(selectedSlot.end_time)}
                      </p>
                    )}
                  </div>

                  {/* Duration */}
                  <div className="p-3 bg-navy-50 dark:bg-navy-700/50 rounded-lg">
                    <p className="text-xs text-navy-500 dark:text-navy-400">Duration</p>
                    <p className="font-medium text-navy-900 dark:text-white">60 minutes</p>
                  </div>

                  {/* Membership Badge */}
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-sm text-emerald-700 dark:text-emerald-400">
                        {hasFullAccess ? 'Included with Membership' : 'Requires Membership'}
                      </span>
                    </div>
                  </div>

                  {/* Book Button */}
                  {hasFullAccess ? (
                    <Button
                      className="w-full bg-amber-600 hover:bg-amber-700"
                      onClick={handleBookSession}
                      disabled={!selectedType || !selectedDate || !selectedSlot || bookingLoading}
                      data-testid="confirm-booking-btn"
                    >
                      {bookingLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Booking...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Confirm Booking
                        </>
                      )}
                    </Button>
                  ) : (
                    <Link to="/membership" className="block">
                      <Button className="w-full bg-amber-600 hover:bg-amber-700">
                        <Lock className="w-4 h-4 mr-2" />
                        Activate Membership
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* My Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-6">
            {/* Upcoming Sessions */}
            <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                  <CalendarIcon className="w-5 h-5 text-amber-600" />
                  Upcoming Sessions
                  {userSessions.upcoming.length > 0 && (
                    <Badge className="bg-amber-600 ml-2">{userSessions.upcoming.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {userSessions.upcoming.length > 0 ? (
                  <div className="space-y-4">
                    {userSessions.upcoming.map((session) => (
                      <div
                        key={session.session_id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-navy-100 dark:border-navy-600 rounded-xl gap-4"
                        data-testid={`session-${session.session_id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <CalendarIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-navy-900 dark:text-white">
                              {getSessionTypeName(session.session_type)}
                            </h4>
                            <p className="text-sm text-navy-500 dark:text-navy-400">
                              with {session.coach_name}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-sm">
                              <span className="flex items-center gap-1 text-navy-600 dark:text-navy-300">
                                <CalendarIcon className="w-4 h-4" />
                                {formatDate(session.date)}
                              </span>
                              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                <Clock className="w-4 h-4" />
                                {formatTime(session.start_time)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            className="dark:border-navy-600 dark:text-white"
                            data-testid="join-session-btn"
                          >
                            <Video className="w-4 h-4 mr-2" />
                            Join Session
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() => handleCancelSession(session.session_id)}
                            data-testid="cancel-session-btn"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CalendarIcon className="w-16 h-16 text-navy-200 dark:text-navy-600 mx-auto mb-4" />
                    <h3 className="font-heading font-semibold text-navy-700 dark:text-navy-300 mb-2">
                      No Upcoming Sessions
                    </h3>
                    <p className="text-navy-500 dark:text-navy-400 mb-4">
                      Book a coaching session to get started
                    </p>
                    <Button
                      className="bg-amber-600 hover:bg-amber-700"
                      onClick={() => setActiveTab('book')}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      Book a Session
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Past Sessions */}
            {userSessions.past.length > 0 && (
              <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                    <Clock className="w-5 h-5 text-navy-500" />
                    Past Sessions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {userSessions.past.map((session) => (
                      <div
                        key={session.session_id}
                        className="flex items-center justify-between p-3 bg-navy-50 dark:bg-navy-700/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            session.status === 'completed' 
                              ? 'bg-emerald-100 dark:bg-emerald-900/50' 
                              : 'bg-red-100 dark:bg-red-900/50'
                          }`}>
                            {session.status === 'completed' ? (
                              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            ) : (
                              <X className="w-5 h-5 text-red-600 dark:text-red-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-navy-900 dark:text-white">
                              {getSessionTypeName(session.session_type)}
                            </p>
                            <p className="text-sm text-navy-500 dark:text-navy-400">
                              {formatDate(session.date)}
                            </p>
                          </div>
                        </div>
                        <Badge
                          variant="outline"
                          className={
                            session.status === 'completed'
                              ? 'border-emerald-200 text-emerald-600 dark:border-emerald-800 dark:text-emerald-400'
                              : 'border-red-200 text-red-600 dark:border-red-800 dark:text-red-400'
                          }
                        >
                          {session.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Coaching;
